import { useCallback, useMemo } from 'react';

import { useApiKey } from 'hooks/configs/use-api-key';
import useEffectOnce from 'hooks/use-effect-once';
import useReadyQueue from 'hooks/use-ready-queue';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isRecentCookieTimestamp } from 'utils/cookies';
import logger from 'utils/logger';
import noop from 'utils/noop';

import { IBranchContext } from '../types';

const useBranch = () => {
  const { drainQueue } = useReadyQueue();
  const enableCookieBanner = useFlag(LaunchDarklyFlag.ENABLE_COOKIE_BANNER);
  const hasAcceptedCookies = isRecentCookieTimestamp();
  const branchKey = useApiKey({ key: 'branch' });

  const initBranch = useCallback(() => {
    import('../init')
      .then(({ default: init }) => {
        init(branchKey, drainQueue);
      })
      .catch(error => {
        logger.error({ error, message: 'Branch: import error' });
      });
  }, [branchKey, drainQueue]);

  const logBranchEvent = noop;

  useEffectOnce(() => {
    if (!enableCookieBanner || hasAcceptedCookies) {
      initBranch();
    }
  });

  const ctxValue: IBranchContext = useMemo(
    () => ({
      logBranchEvent,
      initBranch,
    }),
    [initBranch, logBranchEvent]
  );

  return ctxValue;
};

export default useBranch;

declare global {
  interface Window {
    branch: any;
  }
}
